import { useEffect, useRef } from 'react';

interface BubbleOverlayProps {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  opacity?: number;
}

export default function BubbleOverlay({ colors, opacity = 0.3 }: BubbleOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    const bubbles: Bubble[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    // Accent color RGB, updated each frame from CSS variable
    let accentRgb: [number, number, number] = [10, 116, 255];

    class Bubble {
      x: number;
      y: number;
      radius: number;
      speedY: number;
      speedX: number;
      opacity: number;
      wobble: number;
      wobbleSpeed: number;
      isPrimary: boolean;

      constructor(initialSpawn = false) {
        this.x = 0;
        this.y = 0;
        this.radius = 0;
        this.speedY = 0;
        this.speedX = 0;
        this.opacity = 0;
        this.wobble = 0;
        this.wobbleSpeed = 0;
        this.isPrimary = false;
        this.reset(initialSpawn);
      }

      reset(initialSpawn = false) {
        this.x = Math.random() * canvas.width;
        if (initialSpawn) {
          this.y = Math.random() * canvas.height;
        } else {
          this.y = canvas.height + Math.random() * 50;
        }
        this.radius = Math.random() * 30 + 10;
        this.speedY = (Math.random() * 1 + 0.5) * 0.4;
        this.speedX = (Math.random() - 0.5) * 0.5 * 0.4;
        this.opacity = Math.random() * 0.4 + 0.2;
        this.wobble = Math.random() * Math.PI * 2;
        this.wobbleSpeed = (Math.random() * 0.03 + 0.01) * 0.4;
        this.isPrimary = Math.random() > 0.5;
      }

      update() {
        this.y -= this.speedY;
        this.wobble += this.wobbleSpeed;
        this.x += Math.sin(this.wobble) * 0.5 + this.speedX;

        if (this.y < -this.radius * 2) {
          this.reset();
        }

        if (this.x < -this.radius) this.x = canvas.width + this.radius;
        if (this.x > canvas.width + this.radius) this.x = -this.radius;
      }

      draw() {
        // Use provided colors if passed, otherwise theme accent for both
        let baseColor: string | null = null;
        if (colors?.primary || colors?.secondary) {
          baseColor = (this.isPrimary ? colors?.primary : colors?.secondary) || colors?.primary || colors?.secondary || null;
        }
        const [r, g, b] = baseColor ? parseCssColorToRgb(baseColor) : accentRgb;

        const gradient = ctx.createRadialGradient(
          this.x - this.radius * 0.3,
          this.y - this.radius * 0.3,
          0,
          this.x,
          this.y,
          this.radius
        );
        
        gradient.addColorStop(0, `rgba(${r},${g},${b},${Math.max(0, Math.min(1, this.opacity))})`);
        gradient.addColorStop(0.7, `rgba(${r},${g},${b},${Math.max(0, Math.min(1, this.opacity * 0.3))})`);
        gradient.addColorStop(1, 'transparent');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = `rgba(${r},${g},${b},${Math.max(0, Math.min(1, this.opacity * 0.6))})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }

    for (let i = 0; i < 15; i++) {
      bubbles.push(new Bubble(true));
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      accentRgb = parseCssColorToRgb(accent);

      bubbles.forEach(bubble => {
        bubble.update();
        bubble.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [colors, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
